import {
  PricingSummaryRange,
  Uplift,
  Discount,
  DiscountType,
  Disbursement,
  TaxLabelStatus as vatStatus,
  Range,
  Phase,
  PricingLevel,
  PricingType,
  UpliftType,
  pricingFieldsByType,
  PriceRange,
  PricingOptions,
  PricingSummary,
  joinPricing,
  isSinglePrice,
  GpmType,
  isRangeGpm,
  Work,
  TechnologyCharge,
} from "../../data-types";

import {
  sumLegalFeePriceSingle,
  sumLegalFeePriceRange,
  sumWorkPriceRange,
  sumWorkPriceSingle,
  sumMatterCostRange,
  sumMatterCostSingle,
  calculateGpm,
  calculateGpmRange,
  calculateMixedGpm,
} from "./sum-legal-fee-costs";

import { sumTechnologyChargesSingle, sumTechnologyChargesRange } from "./sum-technology-charges";

export interface TotalStrings<T extends string | number | { lower: number; upper: number }> {
  legalFees: T;
  technologyChargesTotal: T;
  subTotalExcludingVat: T;
  vat: T;
  subTotalIncludingVat: T;
  grandTotal: T;
  disbursementsTotal: T;
  totalDisbursementsTax: T;
  discountsTotal: T;
  upliftsTotal: T;
  adminChargeTotal: T;
  gpm: T;
  cost: T;
}

export enum RoundingOptions {
  None = "None",
  NearestMajorUnit = "NearestMajorUnit", // e.g. pounds, dollars, etc.
  NearestMinorUnit = "NearestMinorUnit", // e.g. pence, cents, etc.
}

export interface UpliftRateAndAmount {
  rate: number;
  amount: number;
}

export function sumUplifts(uplifts: Uplift[]): UpliftRateAndAmount {
  return uplifts.reduce(
    ({ amount, rate }, uplift) => {
      const { amount: currentAmount, type: currentType } = uplift;
      return {
        amount: currentType === UpliftType.Amount ? amount + currentAmount : amount,
        rate: currentType === UpliftType.Percent ? rate + currentAmount : rate,
      };
    },
    { amount: 0, rate: 0 }
  );
}

export function calculatePartialUplift(uplifts: Uplift[]): UpliftRateAndAmount {
  // only uplifts that do NOT appear on the proposal
  const upliftsNotOnProposal = uplifts.filter((u) => !u.includeOnProposal);
  return sumUplifts(upliftsNotOnProposal);
}

export function calculateVisibleUplift(uplifts: Uplift[]): UpliftRateAndAmount {
  // only uplifts that appear on the proposal
  const upliftsOnProposal = uplifts.filter((u) => u.includeOnProposal);
  return sumUplifts(upliftsOnProposal);
}

export function calculateTotalUplift(uplifts: Uplift[]): UpliftRateAndAmount {
  const { amount: partialAmount, rate: partialRate } = calculatePartialUplift(uplifts);
  const { amount: visibleAmount, rate: visibleRate } = calculateVisibleUplift(uplifts);
  const totalUplift = {
    amount: partialAmount + visibleAmount,
    rate: 100 * ((1 + visibleRate / 100) * (1 + partialRate / 100) - 1),
  };
  return totalUplift;
}

export const createFromRangeOrSingle = (args: {
  pricingSummary: PricingOptions;
  phases: Phase[] | null;
  disbursements: Disbursement[] | null;
  uplifts: Uplift[] | null;
  discounts: Discount[] | null;
  technologyCharges: TechnologyCharge[] | null;
  matterWork: Work[] | null;
  pricingLevel: PricingLevel;
  pricingType: PricingType;
  excludeTax: boolean;
  taxLevelPercent: number;
  administrationCharge: number;
  waiveAdminCharge: boolean;
  currencySymbol: string;
}) => {
  const {
    pricingSummary,
    phases,
    disbursements,
    uplifts,
    discounts,
    technologyCharges,
    matterWork,
    pricingLevel,
    pricingType,
    excludeTax,
    taxLevelPercent,
    administrationCharge,
    waiveAdminCharge = false,
    currencySymbol,
  } = args;
  if (isSingle(pricingType)) {
    return createFromSingle<number>(
      pricingSummary,
      phases,
      disbursements,
      uplifts,
      discounts,
      technologyCharges,
      matterWork,
      pricingLevel,
      pricingType,
      excludeTax,
      taxLevelPercent,
      administrationCharge,
      waiveAdminCharge,
      currencySymbol,
      true
    );
  } else {
    const transformed = pricingFieldsByType(PricingType.EstimatedRange, pricingSummary);

    return createFromRange<{ lower: number; upper: number }>(
      transformed as PricingSummaryRange,
      phases,
      disbursements,
      uplifts,
      discounts,
      technologyCharges,
      matterWork,
      pricingLevel,
      pricingType,
      excludeTax,
      taxLevelPercent,
      administrationCharge,
      waiveAdminCharge,
      currencySymbol,
      true
    );
  }
};

export const createTotalsText = (
  pricingSummary: PricingOptions,
  phases: Phase[] | null,
  disbursements: Disbursement[] | null,
  uplifts: Uplift[] | null,
  discounts: Discount[] | null,
  technologyCharges: TechnologyCharge[] | null,
  matterWork: Work[] | null,
  pricingLevel: PricingLevel,
  pricingType: PricingType,
  excludeTax: boolean,
  taxLevelPercent: number,
  administrationCharge: number,
  waiveAdminCharge: boolean,
  currencySymbol: string
): TotalStrings<string> => {
  if (isSingle(pricingType)) {
    return createFromSingle(
      pricingSummary,
      phases,
      disbursements,
      uplifts,
      discounts,
      technologyCharges,
      matterWork,
      pricingLevel,
      pricingType,
      excludeTax,
      taxLevelPercent,
      administrationCharge,
      waiveAdminCharge,
      currencySymbol
    );
  } else {
    const transformed = pricingFieldsByType(PricingType.EstimatedRange, pricingSummary);
    return createFromRange(
      transformed as PricingSummaryRange,
      phases,
      disbursements,
      uplifts,
      discounts,
      technologyCharges,
      matterWork,
      pricingLevel,
      pricingType,
      excludeTax,
      taxLevelPercent,
      administrationCharge,
      waiveAdminCharge,
      currencySymbol
    );
  }
};

const isRange = (pricingType: PricingType): pricingType is PricingType.EstimatedRange => {
  return pricingType === PricingType.EstimatedRange;
};

export const isSingle = (pricingType: PricingType): boolean => {
  return !isRange(pricingType);
};

export const createFromSingle = <T extends number | string = string>(
  pricingSummary: PricingOptions,
  phases: Phase[] | null,
  disbursements: Disbursement[] | null,
  uplifts: Uplift[] | null,
  discounts: Discount[] | null,
  technologyCharges: TechnologyCharge[] | null,
  matterWork: Work[] | null,
  pricingLevel: PricingLevel,
  pricingType: PricingType,
  excludeTax: boolean,
  taxLevelPercent: number,
  administrationCharge: number,
  waiveAdminCharge: boolean,
  currencySymbol: string,
  asNumber?: boolean
): TotalStrings<T> => {
  const transformed = pricingFieldsByType(pricingType, pricingSummary);
  const topDownRateDiscount = pricingSummary?.topDownRateDiscount ?? 0;
  const legalFees = sumLegalFeePriceSingle(
    transformed as PricingSummary,
    phases,
    pricingLevel,
    pricingType,
    matterWork,
    topDownRateDiscount
  );

  // uplifts and discounts apply to legal fees but not technology charges
  const upliftsTotal = calculateTotalUplift(uplifts ?? []);
  const upliftsTotalAmount = (upliftsTotal.rate * legalFees) / 100 + upliftsTotal.amount;
  const upliftsForCalc = legalFees > 0 ? upliftsTotalAmount : 0;

  const discountsTotal =
    discounts?.reduce(
      (acc, { amount, type }) =>
        type === DiscountType.Percent
          ? acc - (amount * (legalFees + upliftsForCalc)) / 100
          : acc - amount,
      0
    ) ?? 0;

  const technologyChargesTotal = sumTechnologyChargesSingle(
    technologyCharges,
    legalFees + upliftsForCalc + discountsTotal
  );

  const subTotalExcludingVatAndAdmin =
    upliftsForCalc + legalFees + discountsTotal + technologyChargesTotal;
  const adminChargeTotal = (administrationCharge * subTotalExcludingVatAndAdmin) / 100;
  const subTotalExcludingVat =
    subTotalExcludingVatAndAdmin + (waiveAdminCharge ? 0 : adminChargeTotal);
  const tax = excludeTax ? 0 : taxLevelPercent / 100;
  const vat = tax * (subTotalExcludingVat || 0);

  const subTotalIncludingVat = (subTotalExcludingVat || 0) + vat;

  const totalTaxableAmount = disbursements
    ?.filter((d) => d.vatStatus !== vatStatus.Exempt)
    .reduce(
      (runningTotal: number, disbursement) =>
        disbursement.vatStatus === vatStatus.Inclusive
          ? // Calculate the amount *without* VAT
            runningTotal + disbursement.amount / (1 + taxLevelPercent / 100)
          : runningTotal + disbursement.amount,
      0
    );

  const disbursementsTotal =
    disbursements?.reduce(
      (acc, curr) =>
        curr.vatStatus === vatStatus.Exclusive && !excludeTax
          ? acc + (1 + tax) * (curr.amount ?? 0)
          : acc + (curr.amount ?? 0),
      0
    ) ?? 0;

  const taxOnDisbursements = disbursementsTotal - (totalTaxableAmount ?? 0);

  const grandTotal = subTotalIncludingVat + disbursementsTotal;

  const cost = sumMatterCostSingle(phases, matterWork, pricingLevel, pricingType);

  const gpm = calculateGpm(cost, subTotalExcludingVat);

  // --- Return values (either precise numbers or formatted strings) ---
  if (asNumber) {
    // Return precise numeric values
    return {
      legalFees: legalFees,
      technologyChargesTotal: technologyChargesTotal,
      subTotalExcludingVat: subTotalExcludingVat,
      vat: vat,
      subTotalIncludingVat: subTotalIncludingVat,
      grandTotal: grandTotal,
      disbursementsTotal: disbursementsTotal,
      totalDisbursementsTax: taxOnDisbursements,
      discountsTotal: discountsTotal,
      upliftsTotal: upliftsTotalAmount,
      adminChargeTotal: adminChargeTotal,
      gpm: gpm?.fixed,
      cost: cost,
    } as TotalStrings<T>;
  } else {
    // Calculate rounded/derived values for display consistency
    const displaySubTotalExcludingVat = Math.round(subTotalExcludingVat);
    const displaySubTotalIncludingVat = parseFloat(subTotalIncludingVat.toFixed(2));
    const displayVat = displaySubTotalIncludingVat - displaySubTotalExcludingVat;

    // Return formatted strings
    return {
      legalFees: convertSingleToString(legalFees, currencySymbol, RoundingOptions.NearestMajorUnit),
      technologyChargesTotal: convertSingleToString(
        technologyChargesTotal,
        currencySymbol,
        RoundingOptions.NearestMajorUnit
      ),
      discountsTotal: convertSingleToString(
        discountsTotal,
        currencySymbol,
        RoundingOptions.NearestMajorUnit
      ),
      disbursementsTotal: convertSingleToString(
        disbursementsTotal,
        currencySymbol,
        RoundingOptions.NearestMinorUnit
      ),
      upliftsTotal: convertSingleToString(
        upliftsTotalAmount,
        currencySymbol,
        RoundingOptions.NearestMajorUnit
      ),
      adminChargeTotal: convertSingleToString(
        adminChargeTotal,
        currencySymbol,
        RoundingOptions.NearestMajorUnit
      ),
      // Use derived display values for these three fields
      subTotalExcludingVat: convertSingleToString(
        displaySubTotalExcludingVat,
        currencySymbol,
        RoundingOptions.NearestMajorUnit
      ),
      vat: convertSingleToString(displayVat, currencySymbol, RoundingOptions.NearestMinorUnit),
      subTotalIncludingVat: convertSingleToString(
        displaySubTotalIncludingVat,
        currencySymbol,
        RoundingOptions.NearestMinorUnit
      ),
      // Use original precise values for formatting the rest
      grandTotal: convertSingleToString(
        grandTotal,
        currencySymbol,
        RoundingOptions.NearestMinorUnit
      ),
      cost: convertSingleToString(cost ?? 0, currencySymbol, RoundingOptions.NearestMajorUnit),
      gpm: convertSingleToString(gpm?.fixed ?? 0, currencySymbol, RoundingOptions.NearestMajorUnit),
      totalDisbursementsTax: convertSingleToString(
        taxOnDisbursements,
        currencySymbol,
        RoundingOptions.NearestMinorUnit
      ),
    } as TotalStrings<T>;
  }
};

export const convertSingleToString = (
  value: number,
  currencySymbol: string,
  rounding: RoundingOptions
): string => {
  switch (rounding) {
    case RoundingOptions.None:
      return `${currencySymbol} ${value.toLocaleString()}`;
    case RoundingOptions.NearestMajorUnit:
      return `${currencySymbol} ${Math.round(value).toLocaleString()}`;
    case RoundingOptions.NearestMinorUnit:
      return `${currencySymbol} ${
        Number.isInteger(value) ? value.toLocaleString() : value.toFixed(2)
      }`;
    default:
      return `${currencySymbol} ${value.toLocaleString()}`;
  }
};

export const createFromRange = <T extends { lower: number; upper: number } | string = string>(
  pricingSummary: PricingSummaryRange,
  phases: Phase[] | null,
  disbursements: Disbursement[] | null,
  uplifts: Uplift[] | null,
  discounts: Discount[] | null,
  technologyCharges: TechnologyCharge[] | null,
  matterWork: Work[] | null,
  pricingLevel: PricingLevel,
  pricingType: PricingType,
  excludeTax: boolean,
  taxLevelPercent: number,
  administrationCharge: number,
  waiveAdminCharge: boolean,
  currencySymbol: string,
  asNumber?: boolean
): TotalStrings<T> => {
  const legalFees = sumLegalFeePriceRange(pricingSummary, phases, pricingLevel);

  // uplifts apply to legal fees but not technology charges
  const upliftsTotal = calculateTotalUplift(uplifts ?? []);
  const upliftsTotalAmount = {
    lower: (upliftsTotal.rate * legalFees.lower) / 100 + upliftsTotal.amount,
    upper: (upliftsTotal.rate * legalFees.upper) / 100 + upliftsTotal.amount,
  };
  const upliftsForCalc =
    legalFees.lower > 0 && legalFees.upper > 0 ? upliftsTotalAmount : { lower: 0, upper: 0 };

  const technologyChargesTotal = sumTechnologyChargesRange(technologyCharges, legalFees);

  const discountsTotal = discounts?.reduce(
    ({ lower, upper }, { amount, type }) => ({
      lower:
        type === DiscountType.Percent
          ? lower -
            (amount * (legalFees.lower + upliftsForCalc.lower + technologyChargesTotal.lower)) / 100
          : lower - (amount + upliftsForCalc.lower),
      upper:
        type === DiscountType.Percent
          ? upper -
            (amount * (legalFees.upper + upliftsForCalc.upper + technologyChargesTotal.upper)) / 100
          : upper - (amount + upliftsForCalc.upper),
    }),
    { lower: 0, upper: 0 }
  ) ?? { lower: 0, upper: 0 };
  const subTotalExcludingVatAndAdmin = {
    lower:
      legalFees.lower + upliftsForCalc.lower + technologyChargesTotal.lower + discountsTotal.lower,
    upper:
      legalFees.upper + upliftsForCalc.upper + technologyChargesTotal.upper + discountsTotal.upper,
  };
  const adminChargeTotal = {
    lower: (administrationCharge * subTotalExcludingVatAndAdmin.lower) / 100,
    upper: (administrationCharge * subTotalExcludingVatAndAdmin.upper) / 100,
  };
  const subTotalExcludingVat = {
    lower: subTotalExcludingVatAndAdmin.lower + (waiveAdminCharge ? 0 : adminChargeTotal.lower),
    upper: subTotalExcludingVatAndAdmin.upper + (waiveAdminCharge ? 0 : adminChargeTotal.upper),
  };
  const tax = excludeTax ? 0 : taxLevelPercent / 100;
  const vat = {
    lower: tax * (subTotalExcludingVat.lower || 0),
    upper: tax * (subTotalExcludingVat.upper || 0),
  };
  const subTotalIncludingVat = {
    lower: subTotalExcludingVat.lower + vat.lower,
    upper: subTotalExcludingVat.upper + vat.upper,
  };
  const disbursementsTotal =
    disbursements?.reduce(
      (acc, curr) =>
        curr.vatStatus === vatStatus.Exclusive
          ? acc + (1 + tax) * (curr.amount ?? 0)
          : acc + (curr.amount ?? 0),
      0
    ) ?? 0;

  const totalTaxableAmount = disbursements
    ?.filter((d) => d.vatStatus !== vatStatus.Exempt)
    .reduce(
      (runningTotal: number, disbursement) =>
        disbursement.vatStatus === vatStatus.Inclusive
          ? // Calculate the amount *without* VAT
            runningTotal + disbursement.amount / (1 + taxLevelPercent / 100)
          : runningTotal + disbursement.amount,
      0
    );

  const taxOnDisbursements = disbursementsTotal - (totalTaxableAmount ?? 0);

  const grandTotal = {
    lower: subTotalIncludingVat.lower + disbursementsTotal,
    upper: subTotalIncludingVat.upper + disbursementsTotal,
  };

  const cost = sumMatterCostRange(phases, matterWork, pricingLevel, pricingType) ?? {
    lower: 0,
    upper: 0,
  };

  const { lower, upper } = calculateGpmRange(cost, subTotalExcludingVat) ?? {
      fixed: 0,
      type: GpmType.Range,
    } ?? { lower: 0, upper: 0, type: GpmType.Range };

  const result = {
    legalFees: asNumber
      ? legalFees
      : convertRangeToString(legalFees, currencySymbol, RoundingOptions.NearestMajorUnit),
    technologyChargesTotal: asNumber
      ? technologyChargesTotal
      : convertRangeToString(
          technologyChargesTotal,
          currencySymbol,
          RoundingOptions.NearestMajorUnit
        ),
    discountsTotal: asNumber
      ? discountsTotal
      : convertRangeToString(discountsTotal, currencySymbol, RoundingOptions.NearestMajorUnit),
    upliftsTotal: asNumber
      ? upliftsTotalAmount
      : convertRangeToString(upliftsTotalAmount, currencySymbol, RoundingOptions.NearestMajorUnit),
    adminChargeTotal: asNumber
      ? adminChargeTotal
      : convertRangeToString(adminChargeTotal, currencySymbol, RoundingOptions.NearestMajorUnit),
    disbursementsTotal: asNumber
      ? disbursementsTotal
      : convertSingleToString(disbursementsTotal, currencySymbol, RoundingOptions.NearestMajorUnit),
    subTotalExcludingVat: asNumber
      ? subTotalExcludingVat
      : convertRangeToString(
          subTotalExcludingVat,
          currencySymbol,
          RoundingOptions.NearestMajorUnit
        ),
    vat: asNumber
      ? vat
      : convertRangeToString(vat, currencySymbol, RoundingOptions.NearestMinorUnit),
    subTotalIncludingVat: asNumber
      ? subTotalIncludingVat
      : convertRangeToString(
          subTotalIncludingVat,
          currencySymbol,
          RoundingOptions.NearestMinorUnit
        ),
    grandTotal: asNumber
      ? grandTotal
      : convertRangeToString(grandTotal, currencySymbol, RoundingOptions.NearestMajorUnit),
    gpm: asNumber
      ? { lower, upper }
      : convertRangeToString(
          { lower: lower ?? 0, upper: upper ?? 0 },
          currencySymbol,
          RoundingOptions.NearestMajorUnit
        ),
    cost: asNumber
      ? cost
      : convertRangeToString(cost, currencySymbol, RoundingOptions.NearestMajorUnit),
    totalDisbursementsTax: asNumber
      ? taxOnDisbursements
      : convertSingleToString(taxOnDisbursements, currencySymbol, RoundingOptions.NearestMinorUnit),
  };

  return result as TotalStrings<T>;
};

const convertRangeToString = (
  range: Range,
  currencySymbol: string,
  rounding: RoundingOptions
): string => {
  const formatValue = (value: number): string => {
    switch (rounding) {
      case RoundingOptions.None:
        return value.toLocaleString();
      case RoundingOptions.NearestMajorUnit:
        return Math.round(value).toLocaleString();
      case RoundingOptions.NearestMinorUnit:
        return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(2);
      default:
        return value.toLocaleString();
    }
  };
  return `${currencySymbol} ${formatValue(range.lower || 0)} to ${currencySymbol} ${formatValue(
    range.upper || 0
  )}`;
};

export const createPhaseTotalText = (
  phase: Phase,
  pricingLevel: PricingLevel,
  pricingType: PricingType,
  currencySymbol: string
): string => {
  if (pricingType === PricingType.None) {
    return "";
  }
  if (pricingLevel === PricingLevel.Phase) {
    if (pricingType === PricingType.EstimatedRange) {
      const transformed = pricingFieldsByType(PricingType.EstimatedRange, phase.priceOptions);
      return convertRangeToString(
        transformed.chosen as PriceRange,
        currencySymbol,
        RoundingOptions.NearestMajorUnit
      );
    } else {
      return convertSingleToString(
        (pricingFieldsByType(pricingType, phase.priceOptions).chosen as number) || 0,
        currencySymbol,
        RoundingOptions.NearestMajorUnit
      );
    }
  } else if (pricingLevel === PricingLevel.FeeEarner) {
    if (pricingType === PricingType.EstimatedRange) {
      return convertRangeToString(
        sumWorkPriceRange(phase.work),
        currencySymbol,
        RoundingOptions.NearestMajorUnit
      );
    } else {
      return convertSingleToString(
        sumWorkPriceSingle(phase.work),
        currencySymbol,
        RoundingOptions.NearestMajorUnit
      );
    }
  }
  return "";
};

type ReduceReturn = {
  fromSingle: TotalStrings<number>[];
  fromRange: TotalStrings<{ lower: number; upper: number }>[];
};

export const createTotalsTextFromMultiplePricingTypes = (
  items:
    | {
        pricingSummary: PricingOptions;
        phases: Phase[] | null;
        disbursements: Disbursement[] | null;
        uplifts: Uplift[] | null;
        discounts: Discount[] | null;
        technologyCharges: TechnologyCharge[] | null;
        matterWork: Work[] | null;
        pricingLevel: PricingLevel;
        pricingType: PricingType;
        excludeTax: boolean;
        taxLevelPercent: number;
        administrationCharge: number;
        waiveAdminCharge: boolean;
      }[]
    | undefined,
  currencySymbol: string
): TotalStrings<string> => {
  if (!items) {
    return {
      legalFees: "",
      technologyChargesTotal: "",
      discountsTotal: "",
      upliftsTotal: "",
      adminChargeTotal: "",
      disbursementsTotal: "",
      totalDisbursementsTax: "",
      subTotalExcludingVat: "",
      vat: "",
      subTotalIncludingVat: "",
      grandTotal: "",
      gpm: "",
      cost: "",
    };
  }

  const collated = items.reduce(
    (acc, curr) => {
      const {
        pricingSummary,
        phases,
        disbursements,
        uplifts,
        discounts,
        technologyCharges,
        matterWork,
        pricingLevel,
        pricingType,
        excludeTax,
        taxLevelPercent,
        administrationCharge,
        waiveAdminCharge,
      } = curr;

      if (isSingle(curr.pricingType)) {
        const fromSingle = createFromSingle<number>(
          pricingSummary,
          phases,
          disbursements,
          uplifts,
          discounts,
          technologyCharges,
          matterWork,
          pricingLevel,
          pricingType,
          excludeTax,
          taxLevelPercent,
          administrationCharge,
          waiveAdminCharge,
          currencySymbol,
          true
        );

        acc.fromSingle.push(fromSingle);
      } else {
        const transformed = pricingFieldsByType(PricingType.EstimatedRange, pricingSummary);
        const fromRange = createFromRange<{ lower: number; upper: number }>(
          transformed as PricingSummaryRange,
          phases,
          disbursements,
          uplifts,
          discounts,
          technologyCharges,
          matterWork,
          pricingLevel,
          pricingType,
          excludeTax,
          taxLevelPercent,
          administrationCharge,
          waiveAdminCharge,
          currencySymbol,
          true
        );

        acc.fromRange.push(fromRange);
      }

      return acc;
    },
    { fromSingle: [], fromRange: [] } as ReduceReturn
  );

  const joinedDisbursements = joinPricing([
    ...collated.fromRange.map((x) => x.disbursementsTotal),
    ...collated.fromSingle.map((x) => x.disbursementsTotal),
  ]);

  const joinedUplifts = joinPricing([
    ...collated.fromRange.map((x) => x.upliftsTotal),
    ...collated.fromSingle.map((x) => x.upliftsTotal),
  ]);

  const joinedDiscounts = joinPricing([
    ...collated.fromRange.map((x) => x.discountsTotal),
    ...collated.fromSingle.map((x) => x.discountsTotal),
  ]);

  const joinedLegalFees = joinPricing([
    ...collated.fromRange.map((x) => x.legalFees),
    ...collated.fromSingle.map((x) => x.legalFees),
  ]);

  const joinedTechnologyChargesTotal = joinPricing([
    ...collated.fromRange.map((x) => x.technologyChargesTotal),
    ...collated.fromSingle.map((x) => x.technologyChargesTotal),
  ]);

  const joinedAdminChargeTotal = joinPricing([
    ...collated.fromRange.map((x) => x.adminChargeTotal),
    ...collated.fromSingle.map((x) => x.adminChargeTotal),
  ]);

  const joinedVat = joinPricing([
    ...collated.fromRange.map((x) => x.vat),
    ...collated.fromSingle.map((x) => x.vat),
  ]);

  const joinedSubTotalIncludingVat = joinPricing([
    ...collated.fromRange.map((x) => x.subTotalIncludingVat),
    ...collated.fromSingle.map((x) => x.subTotalIncludingVat),
  ]);

  const singleAndRangeTotalExcludingVat = [
    ...collated.fromRange.map((x) => x.subTotalExcludingVat),
    ...collated.fromSingle.map((x) => x.subTotalExcludingVat),
  ];

  const totalDisbursementsTax = [
    ...collated.fromRange.map((x) => x.totalDisbursementsTax),
    ...collated.fromSingle.map((x) => x.totalDisbursementsTax),
  ];

  const joinedTotalDisbursementsTax = joinPricing(totalDisbursementsTax);

  const joinedSubTotalExcludingVat = joinPricing(singleAndRangeTotalExcludingVat);

  const joinedGrandTotal = joinPricing([
    ...collated.fromRange.map((x) => x.grandTotal),
    ...collated.fromSingle.map((x) => x.grandTotal),
  ]);

  const singleAndRangedCost = [
    ...collated.fromRange.map((x) => x.cost),
    ...collated.fromSingle.map((x) => x.cost),
  ];
  const joinedCosts = joinPricing(singleAndRangedCost);

  const gpm = calculateMixedGpm({
    cost: singleAndRangedCost,
    price: singleAndRangeTotalExcludingVat,
  });

  let displaySubTotalExcludingVatStr: string;
  let displayVatStr: string;
  let displaySubTotalIncludingVatStr: string;

  if (isSinglePrice(joinedSubTotalExcludingVat) && isSinglePrice(joinedSubTotalIncludingVat)) {
    const displaySubTotalExcludingVatNum = Math.round(joinedSubTotalExcludingVat);
    const displaySubTotalIncludingVatNum = parseFloat(joinedSubTotalIncludingVat.toFixed(2));
    const displayVatNum = displaySubTotalIncludingVatNum - displaySubTotalExcludingVatNum;

    displaySubTotalExcludingVatStr = convertSingleToString(
      displaySubTotalExcludingVatNum,
      currencySymbol,
      RoundingOptions.NearestMajorUnit
    );
    displayVatStr = convertSingleToString(
      displayVatNum,
      currencySymbol,
      RoundingOptions.NearestMinorUnit
    );
    displaySubTotalIncludingVatStr = convertSingleToString(
      displaySubTotalIncludingVatNum,
      currencySymbol,
      RoundingOptions.NearestMinorUnit
    );
  } else {
    displaySubTotalExcludingVatStr = isSinglePrice(joinedSubTotalExcludingVat)
      ? convertSingleToString(
          joinedSubTotalExcludingVat,
          currencySymbol,
          RoundingOptions.NearestMajorUnit
        )
      : convertRangeToString(
          joinedSubTotalExcludingVat,
          currencySymbol,
          RoundingOptions.NearestMajorUnit
        );
    displayVatStr = isSinglePrice(joinedVat)
      ? convertSingleToString(joinedVat, currencySymbol, RoundingOptions.NearestMinorUnit)
      : convertRangeToString(joinedVat, currencySymbol, RoundingOptions.NearestMinorUnit);
    displaySubTotalIncludingVatStr = isSinglePrice(joinedSubTotalIncludingVat)
      ? convertSingleToString(
          joinedSubTotalIncludingVat,
          currencySymbol,
          RoundingOptions.NearestMinorUnit
        )
      : convertRangeToString(
          joinedSubTotalIncludingVat,
          currencySymbol,
          RoundingOptions.NearestMinorUnit
        );
  }

  return {
    disbursementsTotal: isSinglePrice(joinedDisbursements)
      ? convertSingleToString(joinedDisbursements, currencySymbol, RoundingOptions.NearestMajorUnit)
      : convertRangeToString(joinedDisbursements, currencySymbol, RoundingOptions.NearestMajorUnit),
    upliftsTotal: isSinglePrice(joinedUplifts)
      ? convertSingleToString(joinedUplifts, currencySymbol, RoundingOptions.NearestMajorUnit)
      : convertRangeToString(joinedUplifts, currencySymbol, RoundingOptions.NearestMajorUnit),
    discountsTotal: isSinglePrice(joinedDiscounts)
      ? convertSingleToString(joinedDiscounts, currencySymbol, RoundingOptions.NearestMajorUnit)
      : convertRangeToString(joinedDiscounts, currencySymbol, RoundingOptions.NearestMajorUnit),
    legalFees: isSinglePrice(joinedLegalFees)
      ? convertSingleToString(joinedLegalFees, currencySymbol, RoundingOptions.NearestMajorUnit)
      : convertRangeToString(joinedLegalFees, currencySymbol, RoundingOptions.NearestMajorUnit),
    technologyChargesTotal: isSinglePrice(joinedTechnologyChargesTotal)
      ? convertSingleToString(
          joinedTechnologyChargesTotal,
          currencySymbol,
          RoundingOptions.NearestMajorUnit
        )
      : convertRangeToString(
          joinedTechnologyChargesTotal,
          currencySymbol,
          RoundingOptions.NearestMajorUnit
        ),
    adminChargeTotal: isSinglePrice(joinedAdminChargeTotal)
      ? convertSingleToString(
          joinedAdminChargeTotal,
          currencySymbol,
          RoundingOptions.NearestMajorUnit
        )
      : convertRangeToString(
          joinedAdminChargeTotal,
          currencySymbol,
          RoundingOptions.NearestMajorUnit
        ),
    // Assign the derived/formatted strings calculated above
    vat: displayVatStr,
    subTotalIncludingVat: displaySubTotalIncludingVatStr,
    subTotalExcludingVat: displaySubTotalExcludingVatStr,
    // Continue with original logic for other fields
    grandTotal: isSinglePrice(joinedGrandTotal)
      ? convertSingleToString(joinedGrandTotal, currencySymbol, RoundingOptions.NearestMajorUnit)
      : convertRangeToString(joinedGrandTotal, currencySymbol, RoundingOptions.NearestMajorUnit),
    gpm: !gpm
      ? ""
      : isSinglePrice(gpm)
      ? `${gpm.toFixed(2)}%`
      : isRangeGpm(gpm)
      ? `${gpm.lower.toFixed(2)}% - ${gpm.upper.toFixed(2)}%`
      : "",
    cost: isSinglePrice(joinedCosts)
      ? convertSingleToString(joinedCosts, currencySymbol, RoundingOptions.NearestMajorUnit)
      : convertRangeToString(joinedCosts, currencySymbol, RoundingOptions.NearestMajorUnit),
    totalDisbursementsTax: isSinglePrice(joinedTotalDisbursementsTax)
      ? convertSingleToString(
          joinedTotalDisbursementsTax,
          currencySymbol,
          RoundingOptions.NearestMinorUnit
        )
      : convertRangeToString(
          joinedTotalDisbursementsTax,
          currencySymbol,
          RoundingOptions.NearestMinorUnit
        ),
  };
};
